import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box } from "@mui/material";
import { useOrgLabels } from "../../../Hooks/usePagesContent";
import HierarchyFilterDropdown from "./HierarchyFilterDropdown";
import ReadOnlyFilterField from "./ReadOnlyFilterField";
import {
  buildCustomerNodes,
  buildLeafIndex,
  buildLocationTree,
  buildRootExpandedKeys,
  buildSegmentNodes,
  countLeaves,
  deriveSegmentsFromFleetIds,
  fleetIdsForSegments,
  fleetSelectionKey,
  hierarchicalLocationToLeafIds,
  isAppliedLocationStale,
  isEverythingSelected,
  leafIdsToHierarchicalLocation,
  soleLeafName,
} from "./hierarchyFilterUtils";

/**
 * Three independent sibling filters, each with its own trigger, panel and apply
 * cycle. They combine as an AND on the server.
 *
 *   Business segment   flat list of fleet categories
 *   Customer account   flat list of fleets, scoped to the applied segments
 *   Operating location country > state > city > depot, scoped to nothing
 *
 * Only the first two are linked. Applying a segment selection re-scopes the
 * customer list and selects everything in the new scope; the customer selection
 * is what actually travels to the API, so segments never need their own field.
 *
 * All three fields always render, whatever the user's access reaches. Only their
 * interactivity varies: a dropdown when there is more than one option, the single
 * option as static text when there is exactly one, and "-" when there are none.
 * An RBAC-scoped user is often down to one customer account or one depot, and
 * for them the field is the only place the platform says which one - so it is
 * shown rather than hidden.
 *
 * Pass isLoading while the lists are still being fetched. Until then every
 * dimension is empty for a reason that has nothing to do with access, and the
 * no-access dash would be indistinguishable from "not here yet".
 *
 * The props and the onChange({ fleetIds, hierarchicalLocation }) signature are
 * unchanged from the earlier combined control, so consuming pages and the query
 * layer stay as they are.
 */

// A fresh [] on every apply would change the payload identity for a page that
// stores an undefined location, costing one redundant refetch.
const EMPTY_LOCATIONS = [];
const GlobalFilter = ({
  allFleets = [],
  allOnboardingLocationsHierarchy = [],
  selectedFleetIds = [],
  selectedLocations = null,
  onChange,
  direction = "horizontal", // "horizontal" | "vertical"
  isLoading = false,
}) => {
  const isVertical = direction === "vertical";
  const {
    fleetLabel,
    fleetPluralLabel,
    fleetCategoryLabel,
    fleetCategoryPluralLabel,
    locationLabel,
    locationPluralLabel,
  } = useOrgLabels();

  // Call sites pass `allFleets || []` and `globalFilters?.fleets || []`, which
  // hand us a fresh array on every render. Key the memos on content so the
  // dropdowns keep a stable identity and do not re-seed their open draft. The
  // keys are memoised on the incoming reference, so a caller that already
  // passes a stable one pays nothing.
  const allFleetsKey = useMemo(
    () => (allFleets || []).map((fleet) => `${fleet.id}:${fleet.name}:${fleet.category}`).join("|"),
    [allFleets]
  );
  const selectedFleetIdsKey = useMemo(() => fleetSelectionKey(selectedFleetIds), [selectedFleetIds]);
  const selectedLocationsKey = useMemo(() => JSON.stringify(selectedLocations || []), [selectedLocations]);
  const locationHierarchyKey = useMemo(
    () => JSON.stringify(allOnboardingLocationsHierarchy || []),
    [allOnboardingLocationsHierarchy]
  );

  // --- trees ----------------------------------------------------------------
  const segmentNodes = useMemo(
    () => buildSegmentNodes(allFleets),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allFleetsKey]
  );
  const locationNodes = useMemo(
    () => buildLocationTree(allOnboardingLocationsHierarchy),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [locationHierarchyKey]
  );
  const locationLeafIndex = useMemo(() => buildLeafIndex(locationNodes), [locationNodes]);

  // How many options each dimension actually offers, which is what decides
  // between a dropdown and a static field. Counted rather than read off
  // nodes.length because the location tree nests and the flat lists do not.
  const segmentLeafCount = useMemo(() => countLeaves(segmentNodes), [segmentNodes]);
  const locationLeafCount = useMemo(() => countLeaves(locationNodes), [locationNodes]);

  const allFleetIds = useMemo(
    () => (allFleets || []).map((fleet) => fleet.id),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allFleetsKey]
  );

  // --- applied selections ---------------------------------------------------
  // An empty applied fleet list means "all", the same as it always has.
  const propFleetLeafIds = useMemo(
    () => (selectedFleetIdsKey ? [...selectedFleetIds] : allFleetIds),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedFleetIdsKey, allFleetIds]
  );

  // The vertical sidebars drop fleetIds from onChange and never echo a customer
  // selection back, so the container remembers its own last-applied values.
  // A change from the parent always wins — but our own apply echoing back is
  // not a change from the parent, and must not wipe the segment selection.
  const [localFleetLeafIds, setLocalFleetLeafIds] = useState(null);
  const [localSegments, setLocalSegments] = useState(null);
  const emittedFleetIdsKeyRef = useRef(null);

  const resetLocalSelection = () => {
    setLocalFleetLeafIds(null);
    setLocalSegments(null);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(resetLocalSelection, [allFleetsKey]);
  useEffect(() => {
    if (selectedFleetIdsKey === emittedFleetIdsKeyRef.current) return;
    resetLocalSelection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFleetIdsKey]);

  const appliedFleetLeafIds = localFleetLeafIds || propFleetLeafIds;

  // Apply is disabled on an empty panel, so an applied segment list is never
  // empty and `||` is safe here.
  const appliedSegments = useMemo(() => {
    if (localSegments) return localSegments;
    // An applied fleet set that names nothing we know about implies no segment
    // at all. Scoping the customer list to that would empty it, so stay
    // unscoped instead.
    const derived = deriveSegmentsFromFleetIds(appliedFleetLeafIds, allFleets);
    return derived.length ? derived : segmentNodes.map((node) => node.leafId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localSegments, appliedFleetLeafIds, allFleetsKey, segmentNodes]);
  const appliedSegmentsKey = useMemo(() => appliedSegments.join("|"), [appliedSegments]);

  const customerNodes = useMemo(
    () => buildCustomerNodes(allFleets, appliedSegments),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allFleetsKey, appliedSegmentsKey]
  );
  const customerLeafCount = useMemo(() => countLeaves(customerNodes), [customerNodes]);

  const appliedLocationLeafIds = useMemo(
    () => hierarchicalLocationToLeafIds(selectedLocations, locationNodes),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedLocationsKey, locationNodes]
  );

  // An applied payload that names nothing still in the tree keeps filtering the
  // API while the panel has nothing to show for it. Clear it once so the
  // control and the query agree, rather than rendering it as "all selected".
  const staleCorrectionRef = useRef(null);
  useEffect(() => {
    if (!isAppliedLocationStale(selectedLocations, locationNodes)) return;
    const signature = `${selectedLocationsKey}::${locationHierarchyKey}`;
    if (staleCorrectionRef.current === signature) return;
    staleCorrectionRef.current = signature;
    onChange({ fleetIds: selectedFleetIds, hierarchicalLocation: [] });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocationsKey, locationHierarchyKey, locationNodes]);

  // The location panel opens with every country expanded, so the first level
  // under it shows without a click. The two flat lists have nothing to expand.
  const locationDefaultExpanded = useMemo(
    () => buildRootExpandedKeys(locationNodes),
    [locationNodes]
  );

  // --- apply handlers -------------------------------------------------------
  const emit = (fleetIds, hierarchicalLocation) => {
    emittedFleetIdsKeyRef.current = fleetSelectionKey(fleetIds);
    onChange({ fleetIds, hierarchicalLocation });
  };

  // A segment change re-scopes the customer list rather than filtering inside
  // it, so every customer in the new scope comes back selected.
  const handleSegmentApply = (segments) => {
    const fleetIds = fleetIdsForSegments(segments, allFleets);
    setLocalSegments(segments);
    setLocalFleetLeafIds(fleetIds);
    emit(fleetIds, selectedLocations || EMPTY_LOCATIONS);
  };

  const handleCustomerApply = (fleetIds) => {
    setLocalFleetLeafIds(fleetIds);
    emit(fleetIds, selectedLocations || EMPTY_LOCATIONS);
  };

  const handleLocationApply = (leafIds) => {
    const hierarchicalLocation = isEverythingSelected(leafIds, locationNodes)
      ? []
      : leafIdsToHierarchicalLocation(leafIds, locationNodes, locationLeafIndex);
    // Forward the parent's own fleet value untouched so a location change can
    // never look like a customer change to the page above.
    emit(selectedFleetIds, hierarchicalLocation);
  };

  const variant = isVertical ? "accordion" : "popover";

  return (
    <Box
      sx={
        isVertical
          ? { display: "flex", flexDirection: "column", width: "100%" }
          : { display: "flex", alignItems: "center", gap: 1 }
      }
    >
      {segmentLeafCount > 1 ? (
        <HierarchyFilterDropdown
          variant={variant}
          fieldLabel={fleetCategoryLabel}
          panelTitle={fleetCategoryLabel}
          allSelectedLabel={`All ${fleetCategoryPluralLabel}`}
          searchPlaceholder={`Search ${fleetCategoryLabel}`}
          nodes={segmentNodes}
          appliedLeafIds={appliedSegments}
          onApply={handleSegmentApply}
          testId="segment-filter"
        />
      ) : (
        <ReadOnlyFilterField
          variant={variant}
          fieldLabel={fleetCategoryLabel}
          value={soleLeafName(segmentNodes)}
          isLoading={isLoading}
          testId="segment-filter"
        />
      )}
      {customerLeafCount > 1 ? (
        <HierarchyFilterDropdown
          variant={variant}
          fieldLabel={fleetLabel}
          panelTitle={fleetLabel}
          allSelectedLabel={`All ${fleetPluralLabel}`}
          searchPlaceholder={`Search ${fleetLabel}`}
          nodes={customerNodes}
          appliedLeafIds={appliedFleetLeafIds}
          onApply={handleCustomerApply}
          testId="customer-filter"
        />
      ) : (
        <ReadOnlyFilterField
          variant={variant}
          fieldLabel={fleetLabel}
          value={soleLeafName(customerNodes)}
          isLoading={isLoading}
          testId="customer-filter"
        />
      )}
      {locationLeafCount > 1 ? (
        <HierarchyFilterDropdown
          variant={variant}
          fieldLabel={locationLabel}
          panelTitle={locationLabel}
          allSelectedLabel={`All ${locationPluralLabel}`}
          searchPlaceholder={`Search ${locationLabel}`}
          nodes={locationNodes}
          appliedLeafIds={appliedLocationLeafIds}
          defaultExpandedKeys={locationDefaultExpanded}
          onApply={handleLocationApply}
          testId="location-filter"
        />
      ) : (
        <ReadOnlyFilterField
          variant={variant}
          fieldLabel={locationLabel}
          value={soleLeafName(locationNodes)}
          isLoading={isLoading}
          testId="location-filter"
        />
      )}
    </Box>
  );
};

export default GlobalFilter;
